<script setup>
import { computed, ref } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  templates: { type: Object, default: null },
  fallbackOptions: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const search = ref('');
const customBusinessType = ref('');

const normalize = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const UI_LABELS = {
  searchPlaceholder: 'Sök verksamhetstyp',
  fetchError:
    'Kunde inte hämta Chatlotta-listan just nu. Du kan fortfarande välja en grundkategori.',
  loading: 'Laddar verksamhetstyper...',
  popular: 'Populärt',
  noMatch: 'Ingen matchning. Välj annat eller lägg till egen verksamhetstyp.',
  other: 'Annat / jag hittar inte min verksamhetstyp',
  customPlaceholder: 'Lägg till egen verksamhetstyp',
  addCustom: 'Lägg till',
};

const groups = computed(() => props.templates?.groups || []);
const industries = computed(() =>
  Object.values(props.templates?.industries || {})
);
const popularIndustryIds = computed(
  () => props.templates?.popular_industries || []
);

const industryById = computed(() =>
  industries.value.reduce((acc, industry) => {
    acc[industry.id] = industry;
    return acc;
  }, {})
);

const selectedIndustry = computed(() => industryById.value[props.modelValue]);

const fallbackIndustries = computed(() =>
  props.fallbackOptions.map((option, index) => ({
    id: option.value,
    label_sv: option.label,
    label_en: option.label,
    aliases_sv: [],
    group: 'fallback',
    position: index + 1,
  }))
);

const sourceIndustries = computed(() =>
  industries.value.length ? industries.value : fallbackIndustries.value
);

const matchesSearch = industry => {
  const query = normalize(search.value);
  if (!query) return true;

  const searchable = [
    industry.label_sv,
    industry.label_en,
    ...(industry.aliases_sv || []),
    ...(industry.aliases_en || []),
  ]
    .map(normalize)
    .join(' ');

  return searchable.includes(query);
};

const sortedByPosition = items =>
  [...items].sort((a, b) => (a.position || 0) - (b.position || 0));

const filteredIndustries = computed(() =>
  sortedByPosition(sourceIndustries.value.filter(matchesSearch))
);

const popularIndustries = computed(() =>
  sortedByPosition(
    popularIndustryIds.value
      .map(id => industryById.value[id])
      .filter(Boolean)
      .filter(matchesSearch)
  )
);

const groupedIndustries = computed(() => {
  if (!groups.value.length) {
    return [
      {
        id: 'fallback',
        label: 'Business type',
        industries: filteredIndustries.value,
      },
    ];
  }

  return groups.value
    .map(group => ({
      id: group.id,
      label: group.label_sv || group.label_en,
      industries: filteredIndustries.value.filter(
        industry => industry.group === group.id
      ),
    }))
    .filter(group => group.industries.length);
});

const selectIndustry = industryId => {
  emit('update:modelValue', industryId);
};

const selectCustomBusinessType = () => {
  const value = customBusinessType.value.trim();
  emit('update:modelValue', value ? `custom:${value}` : 'custom');
};

const isSelected = industryId => props.modelValue === industryId;
</script>

<template>
  <div class="w-full max-w-full space-y-2">
    <div class="flex items-center justify-end gap-2">
      <Icon icon="i-lucide-search" class="size-4 text-n-slate-9" />
      <Input
        v-model="search"
        size="sm"
        :placeholder="UI_LABELS.searchPlaceholder"
        custom-input-class="min-w-0"
        class="w-full"
      />
    </div>

    <div
      v-if="hasError"
      class="rounded-md border border-n-amber-5 bg-n-amber-2 px-3 py-2 text-sm text-n-slate-11"
    >
      {{ UI_LABELS.fetchError }}
    </div>

    <div
      v-if="selectedIndustry || modelValue"
      class="flex items-center justify-between gap-2 rounded-md bg-n-alpha-2 px-3 py-2"
    >
      <span class="min-w-0 truncate text-sm font-medium text-n-slate-12">
        {{ selectedIndustry?.label_sv || modelValue }}
      </span>
      <NextButton
        type="button"
        icon="i-lucide-x"
        slate
        xs
        ghost
        @click="selectIndustry('')"
      />
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-11">
      {{ UI_LABELS.loading }}
    </div>

    <div v-if="popularIndustries.length" class="space-y-1">
      <div class="text-xs font-medium uppercase text-n-slate-10">
        {{ UI_LABELS.popular }}
      </div>
      <div class="flex flex-wrap gap-1.5">
        <NextButton
          v-for="industry in popularIndustries"
          :key="`popular-${industry.id}`"
          type="button"
          :label="industry.label_sv"
          :color="isSelected(industry.id) ? 'blue' : 'slate'"
          :variant="isSelected(industry.id) ? 'solid' : 'outline'"
          size="xs"
          @click="selectIndustry(industry.id)"
        />
      </div>
    </div>

    <div
      class="max-h-64 space-y-3 overflow-y-auto rounded-md border border-n-weak p-2"
    >
      <div v-for="group in groupedIndustries" :key="group.id" class="space-y-1">
        <div class="px-1 text-xs font-medium uppercase text-n-slate-10">
          {{ group.label }}
        </div>
        <button
          v-for="industry in group.industries"
          :key="industry.id"
          type="button"
          class="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand"
          :class="
            isSelected(industry.id)
              ? 'bg-n-brand/10 text-n-blue-11'
              : 'text-n-slate-12'
          "
          @click="selectIndustry(industry.id)"
        >
          <span class="min-w-0 truncate">{{ industry.label_sv }}</span>
          <Icon
            v-if="isSelected(industry.id)"
            icon="i-lucide-check"
            class="size-4 flex-shrink-0"
          />
        </button>
      </div>

      <div
        v-if="!filteredIndustries.length"
        class="px-2 py-3 text-sm text-n-slate-11"
      >
        {{ UI_LABELS.noMatch }}
      </div>
    </div>

    <div class="rounded-md border border-n-weak p-2 space-y-2">
      <NextButton
        type="button"
        :label="UI_LABELS.other"
        icon="i-lucide-circle-help"
        slate
        outline
        xs
        class="w-full"
        @click="selectIndustry('other')"
      />
      <div class="flex items-center gap-2">
        <Input
          v-model="customBusinessType"
          size="sm"
          :placeholder="UI_LABELS.customPlaceholder"
          class="w-full"
        />
        <NextButton
          type="button"
          :label="UI_LABELS.addCustom"
          slate
          xs
          outline
          @click="selectCustomBusinessType"
        />
      </div>
    </div>
  </div>
</template>
